import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { batch, useSelector, useDispatch } from "react-redux";
import styles from "./SelectionLayer.module.css";
import { CustomIconButton, VerticalDivider, createNotification, icon } from "../../../../../../../common/components";
import {
	setHideFusionDialogDisplay,
	setSelectedRectangles,
	setSelectedSearch,
	setSelectionBarCoordinates,
	setSelectionSidePanelOpen,
	setSidePanel,
} from "../../../../../slice/document/document-slice";
import { SegFlags, isSegFeatureEnabled, translate } from "../../../../../../../common/providers";
import ClassificationDialog from "./dialogs/classification/ClassificationDialog";
import {
	hasAnyMultipleReviewPermissions,
	hasReviewPermissions,
} from "../../../../../permissions/review-permissions-provider";
import REVIEW_PERMISSIONS from "../../../../../permissions/review-permissions";
import { hasUpdateRights } from "../../../../../utils/utils";
import RedressDialog from "../../layers/components/redress-dialog/RedressDialog";
import { areNumbersInArrayConsecutive, debounce, isNonEmptyArray } from "../../../../../../../common/utils";
import { CollabService, RedressmentService } from "../../../../../../../api";
import { resetContent } from "../../../../../slice/pdf/pdf-slice";
import { drawSelectionBox } from "../../../utils/utils";
import { useApi, useTrackEvent } from "../../../../../../../common/hooks";
import RedressAddActions from "../../../../dialogs/redressment/components/RedressAddActions";
import { doesRectOverlap } from "../../../../../utils/coordinates-utils";

const debounceFunc = debounce((func) => func(), 500);

export default function SelectionLayer({
	end,
	pdfContainerRef,
	selectionRectangleRef,
	start,
	onCloseAddRedressment,
	onCloseSelection,
}) {
	const [positionStyle, setPositionStyle] = useState({ top: 0, left: 0 });
	const dispatch = useDispatch();
	const { call: getStringOfContents } = useApi(CollabService.getStringOfContents);
	const { call: proceedRedressInfo } = useApi(RedressmentService.proceedRedressInfo);
	const projectId = useSelector(({ context }) => context.project?.id);
	const [openClassificationDialog, setOpenClassificationDialog] = useState(false);
	const [openRedressDialog, setOpenRedressDialog] = useState(false);
	const [loadingCombination, setLoadingCombination] = useState(false);
	const [isOverlapping, setIsOverLapping] = useState(false);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const mode = useSelector(({ srDocument }) => srDocument.mode);
	const isFrozen = useSelector(({ srDocument }) => srDocument.isFrozen);
	const roles = useSelector(({ srDocument }) => srDocument.roles);
	const selectionBarCoordinates = useSelector(({ srDocument }) => srDocument.selectionBarCoordinates);
	const selectedRectangles = useSelector(({ srDocument }) => srDocument.selectedRectangles);
	const hideFusionDialogDisplay = useSelector(({ srDocument }) => srDocument.hideFusionDialogDisplay);
	const trackEvent = useTrackEvent();
	const addRedressmentFocusActive = useSelector(({ srPdf }) => srPdf.addRedressmentFocusActive);
	const [isDragComplete, setIsDragComplete] = useState(false);

	const addActionRef = useRef(null);
	const debounceRef = useRef(null);
	useEffect(() => {
		if (selectedRectangles.length > 1) {
			dispatch(setSelectionSidePanelOpen(true));
		}
	}, [dispatch, selectedRectangles]);
	useEffect(() => {
		const handleMouseMove = (event) => {
			const divRect = document.getElementById("selectionActionBar")?.getBoundingClientRect();
			if (divRect) {
				const extendedRect = {
					left: divRect.left - 32,
					top: divRect.top - 32,
					right: divRect.right + 32,
					bottom: divRect.bottom + 32,
				};
				const mouseX = event.clientX;
				const mouseY = event.clientY;
				if (
					mouseX < extendedRect.left ||
					mouseX > extendedRect.right ||
					mouseY < extendedRect.top ||
					mouseY > extendedRect.bottom
				) {
					dispatch(setSelectionBarCoordinates(null));
				}
			}
		};
		document.addEventListener("mousemove", handleMouseMove);
		return () => {
			document.removeEventListener("mousemove", handleMouseMove);
		};
	}, [dispatch]);
	const handleOpenClassificationDialog = () => {
		setOpenClassificationDialog(true);
	};
	const handleCloseClassificationDialog = () => {
		setOpenClassificationDialog(false);
	};
	const handleOpenRedressDialog = () => {
		setOpenRedressDialog(true);
	};
	const handleCloseRedressDialog = () => {
		setOpenRedressDialog(false);
	};
	const handleCheck = () => dispatch(setHideFusionDialogDisplay(!hideFusionDialogDisplay));
	const redressingDetails = useMemo(() => {
		if (!isNonEmptyArray(selectedRectangles)) {
			return {};
		}
		const requirement = selectedRectangles.find((p) => p.isReq);
		const rectangles = selectedRectangles.map((p) => p.rectangles).flat();
		const min = Math.min(...rectangles.map((r) => r.order));
		const minContentId = rectangles.find((r) => r.order === min)?.contentId;
		const max = Math.max(...rectangles.map((r) => r.order));
		const redressingId =
			requirement?.informationId || selectedRectangles.find((sr) => sr.contentId === minContentId)?.informationId;
		return { redressingId, min, max };
	}, [selectedRectangles]);
	const handleSubmitRedressDialog = () => {
		const { min, max, redressingId } = redressingDetails;
		if (redressingId && min && max) {
			setLoadingCombination(true);
			proceedRedressInfo(
				{ docId, infoId: redressingId },
				{
					min,
					max,
				}
			)
				.then((pages) => {
					batch(() => {
						dispatch(setSelectionSidePanelOpen(false));
						dispatch(
							setSidePanel({
								sidePanelOpen: false,
								informationId: null,
								page: 0,
								tabToOpen: "",
								isRequirement: null,
							})
						);
						dispatch(setSelectedSearch(null));
						createNotification({
							type: "success",
							message: translate("smart-review.redress-info.success"),
						});
						dispatch(resetContent({ pages, mode }));
						dispatch(setSelectedRectangles(null));
						dispatch(setSelectionBarCoordinates(null));
						trackEvent("MyReview_Redress_Content_Multi");
					});
				})
				.catch((error) => {
					console.error(error);
				})
				.finally(() => {
					if (openRedressDialog) {
						handleCloseRedressDialog();
					}
					setLoadingCombination(false);
				});
		}
	};
	const handleConfirmInfoRedressing = () => {
		if (!hideFusionDialogDisplay) {
			handleOpenRedressDialog();
		} else {
			handleSubmitRedressDialog();
		}
	};
	const isOpenable = useMemo(
		() =>
			!isFrozen &&
			(isSegFeatureEnabled(SegFlags.PROJECT_REQUIREMENTS) || isSegFeatureEnabled(SegFlags.REDRESSEMENT)) &&
			hasUpdateRights(roles) &&
			hasAnyMultipleReviewPermissions(
				[
					REVIEW_PERMISSIONS.CATEGORY_UPDATE,
					REVIEW_PERMISSIONS.CHANGE_TYPE_NO_REVIEW,
					REVIEW_PERMISSIONS.CHANGE_REQ_TO_INFO_WITH_STATUS,
					REVIEW_PERMISSIONS.GROUP_SEPARATE_INFO,
				],
				{ roles }
			),
		[isFrozen, roles]
	);
	const isSelectionContiguous = useMemo(() => {
		const orderIds = selectedRectangles
			.map((p) => p.rectangles)
			.flat()
			.map((p) => p.order);
		return areNumbersInArrayConsecutive(orderIds);
	}, [selectedRectangles]);
	useEffect(
		() => {
			if (start && end && selectionRectangleRef?.current) {
				drawSelectionBox(selectionRectangleRef.current, start, end);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[start, end]
	);
	const requestSelectedContentString = () => {
		if (isNonEmptyArray(selectedRectangles) && projectId) {
			getStringOfContents({ projectId }, { informationIds: selectedRectangles.map((r) => r.informationId) })
				.then((data) => {
					navigator.clipboard.writeText(data.value);
					createNotification({
						message: translate("smart-review.image-container.notification.content-copied"),
					});
				})
				.catch((error) => {
					console.error(error);
				});
		}
	};
	const updatePosition = useCallback(() => {
		if (!selectionRectangleRef.current || !addActionRef.current) {
			return;
		}
		const rect = selectionRectangleRef.current.getBoundingClientRect();
		const actions = addActionRef.current.getBoundingClientRect();
		const container = pdfContainerRef?.current?.getBoundingClientRect();
		let left = rect.width - actions.width;
		let top = rect.height;
		if (
			rect.left - container.left < actions.width &&
			rect.top - container.top < actions.height &&
			container.bottom - rect.bottom < actions.height
		) {
			top = rect.height - actions.height;
			left = rect.width - actions.width;
		}
		if (rect.left - container.left < actions.width && rect.width < actions.width) {
			left = rect.width;
		}
		if (container.bottom - rect.bottom < actions.height) {
			top = -actions.height;
		}
		setPositionStyle({ top, left });
	}, [pdfContainerRef, addActionRef, selectionRectangleRef]);
	useEffect(() => {
		function debouncedUpdate() {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			debounceRef.current = setTimeout(() => {
				updatePosition();
			}, 100);
		}
		updatePosition();
		window.addEventListener("resize", debouncedUpdate);
		return () => {
			window.removeEventListener("resize", debouncedUpdate);
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [start, end, updatePosition, addActionRef]);
	useEffect(() => {
		setIsOverLapping(false);
		debounceFunc(() => {
			setIsOverLapping(
				doesRectOverlap(selectionRectangleRef, "[class^='RenderRectangle_area__']", "any") ||
					doesRectOverlap(selectionRectangleRef, "img[alt^='image_']", "multiple")
			);
		});
	}, [start, end, selectionRectangleRef]);
	useEffect(() => {
		if (start) {
			setIsDragComplete(false);
		}
	}, [start]);
	useEffect(() => {
		const handleMouseUp = () => {
			if (start && end) {
				setIsDragComplete(true);
			}
		};
		document.addEventListener("mouseup", handleMouseUp);
		return () => document.removeEventListener("mouseup", handleMouseUp);
	}, [start, end]);
	useEffect(() => {
		if (isDragComplete) {
			updatePosition();
		}
	}, [isDragComplete, updatePosition]);
	return (
		<>
			{start && end && (
				<div
					ref={selectionRectangleRef}
					className={styles.selectionRectangle}
					data-is-overlapping={isOverlapping}
				>
					{addRedressmentFocusActive && isDragComplete && (
						<RedressAddActions
							addActionRef={addActionRef}
							isOverlapping={isOverlapping}
							positionStyle={positionStyle}
							selectionRectangleRef={selectionRectangleRef}
							onCloseAddRedressment={onCloseAddRedressment}
							onCloseSelection={onCloseSelection}
						/>
					)}
				</div>
			)}
			{selectionBarCoordinates && selectedRectangles.length > 1 && (
				<div
					className={styles.actionBar}
					data-group={hasReviewPermissions(REVIEW_PERMISSIONS.GROUP_SEPARATE_INFO, { roles })}
					id="selectionActionBar"
					style={{
						left: selectionBarCoordinates.x - 44,
						top: selectionBarCoordinates.y - 40,
					}}
				>
					<CustomIconButton
						icon={icon.faCopy}
						size="md"
						tooltip={translate("smart-review.selection-panel.copy-button")}
						onClick={requestSelectedContentString}
					/>
					<>
						{isSegFeatureEnabled(SegFlags.PROJECT_REQUIREMENTS) &&
							hasReviewPermissions(REVIEW_PERMISSIONS.CHANGE_TYPE_NO_REVIEW, { roles }) && (
								<>
									<VerticalDivider />
									<CustomIconButton
										className={styles.actionBtn}
										disabled={!isOpenable}
										icon={icon.faExchangeAlt}
										size="md"
										tooltip={
											(!isOpenable &&
												translate(
													"smart-review.update-sentence-type.title.warning-is-frozen"
												)) ||
											translate("smart-review.update-sentence-type.title.turn-into-req")
										}
										onClick={handleOpenClassificationDialog}
									/>
								</>
							)}
						{isSegFeatureEnabled(SegFlags.REDRESSEMENT) &&
							hasReviewPermissions(REVIEW_PERMISSIONS.GROUP_SEPARATE_INFO, { roles }) && (
								<>
									<VerticalDivider />
									<CustomIconButton
										className={styles.actionBtn}
										data-testid="group.btn"
										disabled={
											!isOpenable || !isSelectionContiguous || selectedRectangles.length < 2
										}
										icon={icon.faObjectGroup}
										size="md"
										tooltip={
											(selectedRectangles.length < 2 &&
												translate(
													"smart-review.selection-bar.group.disabled.insufficient-length"
												)) ||
											(!isSelectionContiguous &&
												translate(
													"smart-review.selection-bar.group.disabled.non-contiguous"
												)) ||
											(!isOpenable &&
												translate("smart-review.cmp-display-overlay.tooltip.redress-frozen")) ||
											translate("smart-review.selection-bar.group")
										}
										onClick={handleConfirmInfoRedressing}
									/>
								</>
							)}
					</>
				</div>
			)}
			<ClassificationDialog open={openClassificationDialog} onClose={handleCloseClassificationDialog} />
			<RedressDialog
				count={selectedRectangles.length}
				hidden={hideFusionDialogDisplay}
				open={openRedressDialog}
				redressingId={redressingDetails?.redressingId}
				submitDisabled={loadingCombination}
				onClose={handleCloseRedressDialog}
				onHide={handleCheck}
				onSubmit={handleSubmitRedressDialog}
			/>
		</>
	);
}
